import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera, CameraOff, Check, ChevronLeft, Cpu, KeyRound, Loader2, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const LS_DEVICE_ID = "cam.device.id";
const LS_DEVICE_TOKEN = "cam.device.token";
const LS_DEVICE_NAME = "cam.device.name";

// --- Browser detection helpers -----------------------------------------------

function detectOs(ua) {
  const u = (ua || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(u)) return "ios";
  if (/android/.test(u)) return "android";
  if (/mac os|macintosh/.test(u)) return "macos";
  if (/windows/.test(u)) return "windows";
  if (/linux/.test(u)) return "linux";
  return "other";
}

function detectType(ua) {
  const u = (ua || "").toLowerCase();
  if (/iphone|ipod/.test(u)) return "mobile";
  if (/ipad|tablet/.test(u)) return "tablet";
  if (/android/.test(u)) return /mobile/.test(u) ? "mobile" : "tablet";
  if (/windows|macintosh|linux/.test(u)) return "laptop";
  return "other";
}

// --- Page ---------------------------------------------------------------------

export default function EnrollPage() {
  const nav = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [permission, setPermission] = useState("idle"); // idle | requesting | granted | denied
  const [previewError, setPreviewError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {device_id, token, device}

  // --- Camera lifecycle ---
  const requestCamera = useCallback(async () => {
    setPermission("requesting");
    setPreviewError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      setPermission("granted");
      // enumerateDevices only returns labels after permission is granted.
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter((d) => d.kind === "videoinput"));
    } catch (e) {
      console.warn("Camera permission denied", e);
      setPermission("denied");
    }
  }, []);

  // Attach the stream to the <video> element AFTER it mounts (React renders
  // the video conditionally on `permission === "granted"`). Doing this inside
  // requestCamera() failed because videoRef.current was still null at that
  // moment. Also explicitly call play() to handle autoplay policies and
  // surface errors to the user.
  useEffect(() => {
    if (permission !== "granted") return;
    const stream = streamRef.current;
    const video = videoRef.current;
    if (!stream || !video) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }
    const p = video.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        console.warn("video.play() failed", err);
        setPreviewError("Impossible de démarrer l'aperçu — cliquez sur la vidéo pour lancer manuellement.");
      });
    }
  }, [permission]);

  // Auto-request on mount if the browser lets us
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("denied");
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const trimmedName = name.trim();
  const nameInvalidReason = (() => {
    if (trimmedName.length === 0) return "Veuillez nommer cet appareil avant d'envoyer la demande.";
    if (trimmedName.length < 3) return "Le nom doit contenir au moins 3 caractères.";
    if (trimmedName.length > 60) return "Le nom ne peut pas dépasser 60 caractères.";
    return null;
  })();
  const canSubmit = !submitting && !nameInvalidReason;

  const submit = async (e) => {
    e.preventDefault();
    if (nameInvalidReason) {
      toast.error(nameInvalidReason);
      return;
    }
    setSubmitting(true);
    const ua = navigator.userAgent || "";
    const payload = {
      name: trimmedName,
      type: detectType(ua),
      os: detectOs(ua),
      user_agent: ua,
      platform: navigator.platform || null,
      language: navigator.language || null,
      screen: window.screen ? { width: window.screen.width, height: window.screen.height } : null,
      cameras: cameras.map((c) => ({ device_id: c.deviceId, label: c.label || "" })),
      camera_permission_granted: permission === "granted",
    };
    try {
      const r = await api.post("/enrollment-requests", payload);
      // Persist for the future when this browser will act as an agent
      try {
        localStorage.setItem(LS_DEVICE_ID, r.data.device_id);
        localStorage.setItem(LS_DEVICE_TOKEN, r.data.token);
        localStorage.setItem(LS_DEVICE_NAME, r.data.device.name);
      } catch {
        // localStorage disabled — non fatal
      }
      setResult(r.data);
      // Stop the camera stream now that the request is sent
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Success view ----------
  if (result) {
    return (
      <div className="min-h-screen bg-obsidian grid-bg flex flex-col" data-testid="enroll-result">
        <TopBar />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-lg panel rounded-none">
            <div className="border-b border-edge px-6 py-4 flex items-center gap-3">
              <div className="size-9 grid place-items-center bg-status-pending/20 text-status-pending border border-status-pending">
                <Loader2 className="size-5 animate-spin" strokeWidth={1.5} />
              </div>
              <div>
                <div className="overline text-status-pending">EN ATTENTE DE VALIDATION</div>
                <h2 className="font-display text-xl font-bold tracking-tight">Demande envoyée</h2>
              </div>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-zinc-400 leading-relaxed">
                L'appareil <span className="text-white font-semibold">{result.device.name}</span> a été
                enregistré avec succès. Il apparaît maintenant dans la <span className="text-brand">salle de contrôle</span> comme
                demande en attente. Un administrateur doit l'approuver pour que cet appareil devienne actif.
              </p>
              <div className="border border-edge bg-obsidian px-4 py-3 space-y-1 text-xs font-mono">
                <Row k="ID appareil" v={result.device_id.slice(0, 8) + "…"} />
                <Row k="Nom" v={result.device.name} />
                <Row k="Statut" v="pending" />
                <Row k="Jeton local" v="✓ stocké dans ce navigateur" />
              </div>
              <div className="border border-status-pending/40 bg-status-pending/5 px-3 py-2 text-[11px] text-status-pending font-mono">
                Cet appareil ne diffusera aucun flux tant qu'il n'est pas approuvé.
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => nav("/select")}
                  className="rounded-none border-edge bg-transparent flex-1"
                  data-testid="enroll-back-select"
                >
                  <ChevronLeft className="size-3.5 mr-1.5" strokeWidth={1.5} /> RETOUR
                </Button>
                <Button
                  onClick={() => nav("/control-room")}
                  className="rounded-none bg-brand hover:bg-brand-hover flex-1"
                  data-testid="enroll-go-control"
                >
                  SALLE DE CONTRÔLE
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Form view ----------
  return (
    <div className="min-h-screen bg-obsidian grid-bg flex flex-col" data-testid="enroll-page">
      <TopBar onBack={() => nav("/select")} />
      <div className="flex-1 flex items-start justify-center p-4">
        <form onSubmit={submit} className="w-full max-w-3xl grid md:grid-cols-2 gap-4">
          {/* Left — preview */}
          <div className="panel rounded-none overflow-hidden" data-testid="enroll-camera-panel">
            <div className="border-b border-edge px-4 py-2 overline flex items-center justify-between">
              <span>APERÇU CAMÉRA</span>
              <span className={`text-[10px] font-mono ${
                permission === "granted" ? "text-status-online" :
                permission === "denied" ? "text-status-offline" :
                permission === "requesting" ? "text-status-pending" : "text-zinc-500"
              }`} data-testid="enroll-permission-state">
                {permission === "granted" && "● AUTORISÉE"}
                {permission === "denied"  && "● REFUSÉE"}
                {permission === "requesting" && "DEMANDE..."}
                {permission === "idle" && "NON DEMANDÉE"}
              </span>
            </div>

            <div className="relative aspect-video bg-obsidian grid-bg scanlines">
              {permission === "granted" ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    onClick={() => {
                      try { videoRef.current?.play(); setPreviewError(null); } catch (e) { /* noop */ }
                    }}
                    className="w-full h-full object-cover bg-black cursor-pointer"
                    data-testid="enroll-video"
                  />
                  {previewError && (
                    <div className="absolute bottom-0 left-0 right-0 bg-status-offline/90 text-white text-[11px] font-mono px-3 py-1.5" data-testid="enroll-preview-error">
                      {previewError}
                    </div>
                  )}
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 p-6 text-center">
                  {permission === "denied" ? (
                    <>
                      <CameraOff className="size-8 mb-2" strokeWidth={1} />
                      <div className="overline text-status-offline mb-1">ACCÈS CAMÉRA REFUSÉ</div>
                      <p className="text-xs text-zinc-500 max-w-xs">
                        Vous pouvez continuer sans caméra, ou autoriser l'accès dans les réglages du navigateur.
                      </p>
                    </>
                  ) : (
                    <>
                      <Camera className="size-8 mb-2" strokeWidth={1} />
                      <div className="overline mb-1">CAMÉRA REQUISE</div>
                      <p className="text-xs text-zinc-500 mb-4 max-w-xs">
                        Autorisez l'accès à la caméra pour vérifier la source avant enrôlement.
                      </p>
                      <Button
                        type="button"
                        onClick={requestCamera}
                        disabled={permission === "requesting"}
                        className="rounded-none bg-brand hover:bg-brand-hover"
                        data-testid="enroll-request-camera"
                      >
                        {permission === "requesting" ? "DEMANDE..." : "AUTORISER LA CAMÉRA"}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Enumerated cameras */}
            <div className="border-t border-edge px-4 py-3">
              <div className="overline mb-2 flex items-center gap-1.5">
                <Cpu className="size-3" strokeWidth={1.5} /> CAMÉRAS DÉTECTÉES ({cameras.length})
              </div>
              {cameras.length === 0 ? (
                <div className="text-[11px] text-zinc-500 font-mono">
                  {permission === "granted" ? "Aucune caméra détectée." : "Autorisez la caméra pour lister les sources."}
                </div>
              ) : (
                <ul className="space-y-1" data-testid="enroll-camera-list">
                  {cameras.map((c, i) => (
                    <li key={c.deviceId || i} className="text-[11px] font-mono text-zinc-300 truncate" data-testid={`enroll-camera-${i}`}>
                      <Check className="size-3 inline mr-1.5 text-status-online" strokeWidth={2} />
                      {c.label || `Caméra ${i + 1}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Right — form */}
          <div className="panel rounded-none">
            <div className="border-b border-edge px-4 py-2 overline">CONFIGURATION</div>
            <div className="px-4 py-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="overline" htmlFor="enroll-name">NOM DE L'APPAREIL</Label>
                <Input
                  id="enroll-name"
                  data-testid="enroll-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  minLength={3}
                  required
                  placeholder="Ex: Téléphone garage"
                  className={`rounded-none bg-obsidian border-edge font-mono text-sm ${nameInvalidReason && trimmedName.length > 0 ? "border-status-offline" : ""}`}
                />
                {nameInvalidReason && trimmedName.length > 0 ? (
                  <p className="text-[10px] text-status-offline font-mono" data-testid="enroll-name-error">
                    {nameInvalidReason}
                  </p>
                ) : (
                  <p className="text-[10px] text-zinc-600">
                    Ce nom identifiera l'appareil dans la salle de contrôle. 3 à 60 caractères.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="overline">INFOS DÉTECTÉES</Label>
                <div className="border border-edge bg-obsidian px-3 py-2 space-y-1 text-[11px] font-mono">
                  <Row k="Type" v={detectType(navigator.userAgent)} />
                  <Row k="OS" v={detectOs(navigator.userAgent)} />
                  <Row k="Navigateur" v={(navigator.userAgent || "").split(" ").slice(-1)[0] || "—"} />
                  <Row k="Langue" v={navigator.language || "—"} />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="enroll-submit"
                className="w-full rounded-none bg-brand hover:bg-brand-hover disabled:opacity-50"
              >
                <KeyRound className="size-4 mr-2" strokeWidth={2} />
                {submitting ? "ENVOI..." : "ENVOYER LA DEMANDE"}
              </Button>

              <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
                Un jeton sera généré côté serveur et stocké localement dans ce navigateur.
                L'appareil restera <span className="text-status-pending">en attente</span> jusqu'à validation par l'administrateur.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function TopBar({ onBack }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };
  return (
    <div className="border-b border-edge px-4 h-14 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {onBack && (
          <Button variant="outline" size="sm" onClick={onBack} className="rounded-none border-edge bg-transparent" data-testid="enroll-back">
            <ChevronLeft className="size-3.5 mr-1.5" strokeWidth={1.5} /> SÉLECTION
          </Button>
        )}
        <div className="flex items-center gap-2 min-w-0">
          <Camera className="size-4 text-brand shrink-0" strokeWidth={1.5} />
          <span className="font-display font-bold tracking-tight truncate">Enrôler cet appareil</span>
        </div>
      </div>
      <button
        onClick={handleLogout}
        data-testid="enroll-logout"
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-edge hover:border-status-offline hover:text-status-offline text-zinc-400 transition-colors"
        aria-label="Se déconnecter"
      >
        <LogOut className="size-3.5" strokeWidth={1.5} />
        <span className="hidden sm:inline">DÉCONNEXION</span>
      </button>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-zinc-500 uppercase tracking-wider text-[10px]">{k}</span>
      <span className="text-zinc-200 truncate">{v}</span>
    </div>
  );
}
